import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
} from 'lexical';
import {
  $createHeadingNode,
  HeadingTagType,
} from '@lexical/rich-text';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode,
} from '@lexical/list';
import { $getNearestNodeOfType } from '@lexical/utils';
import { $setBlocksType } from '@lexical/selection';
import { $createParagraphNode } from 'lexical';

interface ToolbarPluginProps { mode?: 'full' | 'inline'; }

const ToolbarPlugin = ({ mode = 'full' }: ToolbarPluginProps) => {
  const [editor] = useLexicalComposerContext();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const [isBold, setIsBold] = React.useState(false);
  const [isItalic, setIsItalic] = React.useState(false);
  const [isUnderline, setIsUnderline] = React.useState(false);
  const [isStrikethrough, setIsStrikethrough] = React.useState(false);
  const [isSubscript, setIsSubscript] = React.useState(false);
  const [isSuperscript, setIsSuperscript] = React.useState(false);
  const [listType, setListType] = React.useState<'bullet' | 'number' | null>(null);

  const updateToolbar = React.useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsSubscript(selection.hasFormat('subscript'));
      setIsSuperscript(selection.hasFormat('superscript'));

      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
      const parentList = $getNearestNodeOfType(anchorNode, ListNode);
      if (parentList) {
        setListType($isListNode(parentList) && parentList.getListType() === 'number' ? 'number' : 'bullet');
      } else {
        setListType(null);
      }
    }
  }, []);

  React.useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatHeading = (headingTag: HeadingTagType) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createHeadingNode(headingTag));
      }
    });
  };

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  // inline 模式：只顯示 Bold / Italic / Underline / Subscript / Superscript
  if (mode === 'inline') {
    return (
      <div className="toolbar">
        <button type="button" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
          className={`toolbar-item ${isBold ? 'active' : ''}`} aria-label="Bold">
          <strong>B</strong>
        </button>
        <button type="button" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
          className={`toolbar-item ${isItalic ? 'active' : ''}`} aria-label="Italic">
          <em>I</em>
        </button>
        <button type="button" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
          className={`toolbar-item ${isUnderline ? 'active' : ''}`} aria-label="Underline">
          <u>U</u>
        </button>
        <span className="toolbar-divider" />
        <button type="button" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript')}
          className={`toolbar-item ${isSubscript ? 'active' : ''}`} aria-label="Subscript">
          X<sub>₂</sub>
        </button>
        <button type="button" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript')}
          className={`toolbar-item ${isSuperscript ? 'active' : ''}`} aria-label="Superscript">
          X<sup>²</sup>
        </button>
      </div>
    );
  }

  // full 模式：完整工具列
  return (
    <div className="toolbar">
      <button type="button" onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)} className="toolbar-item" aria-label="Undo">↶</button>
      <button type="button" onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)} className="toolbar-item" aria-label="Redo">↷</button>
      <span className="toolbar-divider" />
      <select className="toolbar-item" onChange={(e) => {
        const value = e.target.value;
        if (value === 'paragraph') { formatParagraph(); } else { formatHeading(value as HeadingTagType); }
      }}>
        <option value="paragraph">{isZh ? '段落' : 'Paragraph'}</option>
        <option value="h1">{isZh ? '標題 1' : 'Heading 1'}</option>
        <option value="h2">{isZh ? '標題 2' : 'Heading 2'}</option>
        <option value="h3">{isZh ? '標題 3' : 'Heading 3'}</option>
      </select>
      <span className="toolbar-divider" />
      <button type="button" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')} className={`toolbar-item ${isBold ? 'active' : ''}`} aria-label="Bold"><strong>B</strong></button>
      <button type="button" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')} className={`toolbar-item ${isItalic ? 'active' : ''}`} aria-label="Italic"><em>I</em></button>
      <button type="button" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')} className={`toolbar-item ${isUnderline ? 'active' : ''}`} aria-label="Underline"><u>U</u></button>
      <button type="button" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')} className={`toolbar-item ${isStrikethrough ? 'active' : ''}`} aria-label="Strikethrough"><s>S</s></button>
      <button type="button" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript')} className={`toolbar-item ${isSubscript ? 'active' : ''}`} aria-label="Subscript">X<sub>₂</sub></button>
      <button type="button" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript')} className={`toolbar-item ${isSuperscript ? 'active' : ''}`} aria-label="Superscript">X<sup>²</sup></button>
      <span className="toolbar-divider" />
      <button type="button" onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')} className="toolbar-item" aria-label="Left Align">≡</button>
      <button type="button" onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')} className="toolbar-item" aria-label="Center Align">≣</button>
      <button type="button" onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')} className="toolbar-item" aria-label="Right Align">≡</button>
      <span className="toolbar-divider" />
      <button type="button" onClick={() => {
        if (listType === 'bullet') { editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined); }
        else { editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined); }
      }} className={`toolbar-item ${listType === 'bullet' ? 'active' : ''}`} aria-label="Bullet List">{isZh ? '• 列表' : '• List'}</button>
      <button type="button" onClick={() => {
        if (listType === 'number') { editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined); }
        else { editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined); }
      }} className={`toolbar-item ${listType === 'number' ? 'active' : ''}`} aria-label="Numbered List">{isZh ? '1. 列表' : '1. List'}</button>
    </div>
  );
};

export default ToolbarPlugin;
